import React, { useState, useEffect } from 'react';
import { attendanceAPI, overtimeAPI } from '../../services/api';
import { format, subMonths, addMonths, setDate } from 'date-fns';
import { Calendar, Clock, RefreshCw, X, Search, CheckCircle, XCircle } from 'lucide-react';
import { getStatusBadge, getRowHighlightClass, DateColumnDisplay, formatMinutes } from '../../utils/attendanceUtils';

// ─── Helpers ──────────────────────────────────────────────────────────────────

const defaultStart = () => format(setDate(subMonths(new Date(), 1), 21), 'yyyy-MM-dd');
const defaultEnd   = () => format(setDate(addMonths(new Date(), 1), 20), 'yyyy-MM-dd');

const toLocalISO = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return new Date(d - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
};

const formatTime = (iso) =>
  iso ? new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '-';

// ─── Team Attendance Tab ───────────────────────────────────────────────────────

const PAGE_SIZE = 50;

const TeamAttendanceTab = () => {
  const [startDate, setStartDate] = useState(defaultStart());
  const [endDate, setEndDate]     = useState(defaultEnd());
  const [records, setRecords]     = useState([]);
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState('');
  const [currentPage, setCurrentPage] = useState(1);

  const [teamUsers, setTeamUsers]           = useState([]); // stable list derived from unfiltered fetch
  const [userSearchTerm, setUserSearchTerm] = useState('');
  const [selectedUserId, setSelectedUserId] = useState('');
  const [showDropdown, setShowDropdown]     = useState(false);

  // On mount, fetch the full date range with no user filter to populate team member list
  useEffect(() => {
    const loadTeamUsers = async () => {
      try {
        const res = await attendanceAPI.getManagerTeamRecords({ start_date: defaultStart(), end_date: defaultEnd() });
        const records = res.data.records || [];
        const seen = new Map();
        records.forEach(r => { if (!seen.has(r.user_id)) seen.set(r.user_id, { id: r.user_id, name: r.name, staff_no: r.staff_no }); });
        setTeamUsers(Array.from(seen.values()).sort((a, b) => a.name.localeCompare(b.name)));
      } catch {}
    };
    loadTeamUsers();
  }, []);

  useEffect(() => { fetchRecords(); }, [startDate, endDate, selectedUserId]);

  const fetchRecords = async () => {
    setLoading(true);
    setError('');
    setCurrentPage(1);
    try {
      const params = { start_date: startDate, end_date: endDate };
      if (selectedUserId) params.user_id = selectedUserId;
      const res = await attendanceAPI.getManagerTeamRecords(params);
      // Sort records by date (latest first), and by user staff_no (ascending)
      const sorted = (res.data.records || []).sort((a, b) => {
        const dateDiff = new Date(b.workday) - new Date(a.workday);
        if (dateDiff !== 0) return dateDiff;
        return a.staff_no.localeCompare(b.staff_no);
      });
      setRecords(sorted);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to fetch records');
    } finally {
      setLoading(false);
    }
  };

  const totalPages = Math.ceil(records.length / PAGE_SIZE);
  const paginatedRecords = records.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  return (
    <div className="space-y-6">
      {/* Controls */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center space-y-4 md:space-y-0">
        <h2 className="text-xl font-bold text-gray-900">Team Attendance</h2>
        <div className="flex flex-col sm:flex-row space-y-3 sm:space-y-0 sm:space-x-3 w-full md:w-auto">
          {/* User search */}
          <div
            className="relative min-w-[220px]"
            onBlur={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setShowDropdown(false); }}
          >
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search className="h-4 w-4 text-gray-400" />
            </div>
            <input
              type="text"
              value={userSearchTerm}
              onChange={(e) => { setUserSearchTerm(e.target.value); setShowDropdown(true); }}
              onFocus={() => setShowDropdown(true)}
              placeholder="Filter by employee..."
              className="pl-9 pr-8 py-2 block w-full border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
            />
            {(userSearchTerm || selectedUserId) && (
              <button
                className="absolute inset-y-0 right-0 pr-2 flex items-center text-gray-400 hover:text-gray-600"
                onMouseDown={(e) => { e.preventDefault(); setUserSearchTerm(''); setSelectedUserId(''); }}
              >
                <X className="h-4 w-4" />
              </button>
            )}
            {showDropdown && (
              <ul className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg max-h-48 overflow-y-auto text-sm">
                <li
                  className="px-3 py-2 cursor-pointer hover:bg-gray-100 text-gray-500"
                  onMouseDown={() => { setSelectedUserId(''); setUserSearchTerm(''); setShowDropdown(false); }}
                >
                  All Team Members
                </li>
                {teamUsers
                  .filter(u => !userSearchTerm || u.name.toLowerCase().includes(userSearchTerm.toLowerCase()) || u.staff_no.toLowerCase().includes(userSearchTerm.toLowerCase()))
                  .map(u => (
                    <li
                      key={u.id}
                      className="px-3 py-2 cursor-pointer hover:bg-blue-50 hover:text-blue-700"
                      onMouseDown={() => { setSelectedUserId(String(u.id)); setUserSearchTerm(`${u.name} (${u.staff_no})`); setShowDropdown(false); }}
                    >
                      {u.name} <span className="text-gray-400">({u.staff_no})</span>
                    </li>
                  ))}
              </ul>
            )}
          </div>

          <div className="flex items-center space-x-2 bg-white rounded-md border border-gray-300 px-2 shadow-sm">
            <span className="text-sm text-gray-500 font-medium">From</span>
            <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)}
              className="border-0 focus:ring-0 sm:text-sm py-2 px-1 text-gray-900" />
          </div>
          <div className="flex items-center space-x-2 bg-white rounded-md border border-gray-300 px-2 shadow-sm">
            <span className="text-sm text-gray-500 font-medium">To</span>
            <input type="date" value={endDate} min={startDate} onChange={(e) => setEndDate(e.target.value)}
              className="border-0 focus:ring-0 sm:text-sm py-2 px-1 text-gray-900" />
          </div>
          <button onClick={fetchRecords}
            className="inline-flex items-center justify-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50">
            <RefreshCw className="h-4 w-4 md:mr-2" /><span className="hidden md:inline">Refresh</span>
          </button>
        </div>
      </div>

      {error && <div className="bg-red-50 border-l-4 border-red-400 p-4"><p className="text-sm text-red-700">{error}</p></div>}

      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="relative overflow-x-auto">
          {loading ? (
            <div className="p-8 text-center text-gray-500">Loading records...</div>
          ) : records.length === 0 ? (
            <div className="p-12 text-center text-gray-500">No records found for this period.</div>
          ) : (
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Employee</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Shift</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Clock In</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Clock Out</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Hrs</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {paginatedRecords.map((record) => (
                  <tr key={`${record.user_id}_${record.workday}`} className={getRowHighlightClass(record.workday, record.is_final)}>
                    <td className="px-6 py-4 whitespace-nowrap"><DateColumnDisplay workday={record.workday} /></td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{record.name}</div>
                      <div className="text-sm text-gray-500">{record.staff_no}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {record.shift_name ? `${record.shift_name} (${record.shift_code})` : 'Unassigned'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{formatTime(record.clock_in)}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{formatTime(record.clock_out)}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatMinutes(record.total_work_minutes)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">{getStatusBadge(record)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {totalPages > 1 && (
        <div className="bg-white px-4 py-3 flex items-center justify-between border border-gray-200 rounded-lg shadow-sm">
          <p className="text-sm text-gray-700">
            Showing <span className="font-medium">{(currentPage - 1) * PAGE_SIZE + 1}</span>–
            <span className="font-medium">{Math.min(currentPage * PAGE_SIZE, records.length)}</span> of{' '}
            <span className="font-medium">{records.length}</span> records
          </p>
          <div className="flex items-center space-x-1">
            <button
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="px-3 py-1 rounded border border-gray-300 text-sm text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1)
              .filter(p => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 1)
              .reduce((acc, p, idx, arr) => {
                if (idx > 0 && p - arr[idx - 1] > 1) acc.push('...');
                acc.push(p);
                return acc;
              }, [])
              .map((item, idx) =>
                item === '...' ? (
                  <span key={`ellipsis-${idx}`} className="px-2 py-1 text-sm text-gray-400">…</span>
                ) : (
                  <button
                    key={item}
                    onClick={() => setCurrentPage(item)}
                    className={`px-3 py-1 rounded border text-sm ${
                      currentPage === item
                        ? 'bg-blue-600 border-blue-600 text-white font-medium'
                        : 'border-gray-300 text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    {item}
                  </button>
                )
              )}
            <button
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className="px-3 py-1 rounded border border-gray-300 text-sm text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Overtime Approvals Tab ────────────────────────────────────────────────────

const OvertimeApprovalsTab = () => {
  const [requests, setRequests]         = useState([]);
  const [loading, setLoading]           = useState(false);
  const [error, setError]               = useState('');
  const [success, setSuccess]           = useState('');
  const [statusFilter, setStatusFilter] = useState('pending');
  const [selectedIds, setSelectedIds]   = useState(new Set());

  const [remarkModal, setRemarkModal]   = useState(null); // { ids: [], status: '' }
  const [remark, setRemark]             = useState('');

  const [startDate, setStartDate] = useState(defaultStart());
  const [endDate, setEndDate]     = useState(defaultEnd());
  const [deptFilter, setDeptFilter] = useState('');

  const [expandedRemarks, setExpandedRemarks] = useState(new Set());
  const toggleRemark = (id) => setExpandedRemarks(prev => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  useEffect(() => {
    setDeptFilter('');
    setSelectedIds(new Set());
    fetchRequests();
  }, [statusFilter]);

  const fetchRequests = async () => {
    setLoading(true);
    setError('');
    try {
      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      const res = await overtimeAPI.getAllOvertimeRequests(params);
      setRequests(res.data.overtime_requests || []);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to fetch overtime requests');
    } finally {
      setLoading(false);
    }
  };

  const toggleSelect = (id) => {
    const next = new Set(selectedIds);
    next.has(id) ? next.delete(id) : next.add(id);
    setSelectedIds(next);
  };

  const toggleAll = () => {
    if (selectedIds.size === pendingIds.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(pendingIds));
    }
  };

  const openApprove = (ids, status) => {
    setRemark('');
    setRemarkModal({ ids, status });
  };

  const submitApproval = async () => {
    const { ids, status } = remarkModal;
    setLoading(true);
    setError('');
    try {
      if (ids.length === 1) {
        await overtimeAPI.approveOvertime(ids[0], status, remark);
      } else {
        await overtimeAPI.bulkApproveOvertime(ids, status, remark);
      }
      setSuccess(`${ids.length} request(s) ${status}.`);
      setRemarkModal(null);
      fetchRequests();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to process approval');
      setLoading(false);
    }
  };

  const departments = [...new Set(requests.map(r => r.department_name).filter(Boolean))].sort();
  const filteredRequests = requests.filter(r => {
    if (startDate && r.date < startDate) return false;
    if (endDate && r.date > endDate) return false;
    if (deptFilter && r.department_name !== deptFilter) return false;
    return true;
  });
  const pendingIds = filteredRequests.filter(r => r.status === 'pending').map(r => r.id);
  const formatDT = (iso) => iso ? new Date(iso).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-';
  const calcDuration = (start, end, breakMins) => {
    if (!start || !end) return '-';
    const hours = Math.max(0, (new Date(end) - new Date(start)) / 3600000 - (breakMins || 0) / 60);
    return `${hours.toFixed(1)}h`;
  };

  const statusBadge = (status) => {
    const styles = { pending: 'bg-yellow-100 text-yellow-800', approved: 'bg-green-100 text-green-800', rejected: 'bg-red-100 text-red-800', cancelled: 'bg-gray-100 text-gray-800' };
    return <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${styles[status] || ''}`}>{status}</span>;
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center space-y-4 md:space-y-0">
        <h2 className="text-xl font-bold text-gray-900">Overtime Approvals</h2>
        <div className="flex flex-wrap gap-3 items-center">
          <input type="date" value={startDate} onChange={e => { setStartDate(e.target.value); setSelectedIds(new Set()); }}
            className="border border-gray-300 rounded-md py-2 px-3 text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500" />
          <span className="text-gray-400 text-sm">to</span>
          <input type="date" value={endDate} onChange={e => { setEndDate(e.target.value); setSelectedIds(new Set()); }}
            className="border border-gray-300 rounded-md py-2 px-3 text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500" />
          <select
            value={deptFilter}
            onChange={e => { setDeptFilter(e.target.value); setSelectedIds(new Set()); }}
            className="block border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm py-2 px-3"
          >
            <option value="">All Departments</option>
            {departments.map(d => <option key={d} value={d}>{d}</option>)}
          </select>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="block border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm py-2 px-3"
          >
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
            <option value="all">All</option>
          </select>
          <button onClick={fetchRequests}
            className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50">
            <RefreshCw className="h-4 w-4 md:mr-2" /><span className="hidden md:inline">Refresh</span>
          </button>
        </div>
      </div>

      {error && <div className="bg-red-50 border-l-4 border-red-400 p-4"><p className="text-sm text-red-700">{error}</p></div>}
      {success && <div className="bg-green-50 border-l-4 border-green-400 p-4"><p className="text-sm text-green-700">{success}</p></div>}

      {/* Bulk actions */}
      {statusFilter === 'pending' && (
        <div className="bg-white p-4 shadow rounded-lg flex justify-between items-center">
          <span className="text-sm text-gray-700"><span className="font-semibold">{selectedIds.size}</span> selected</span>
          <div className="space-x-3">
            <button
              disabled={selectedIds.size === 0}
              onClick={() => openApprove(Array.from(selectedIds), 'approved')}
              className="inline-flex items-center px-4 py-2 text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 disabled:opacity-50"
            >
              <CheckCircle className="h-4 w-4 mr-2" /> Approve Selected
            </button>
            <button
              disabled={selectedIds.size === 0}
              onClick={() => openApprove(Array.from(selectedIds), 'rejected')}
              className="inline-flex items-center px-4 py-2 text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 disabled:opacity-50"
            >
              <XCircle className="h-4 w-4 mr-2" /> Reject Selected
            </button>
          </div>
        </div>
      )}

      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="relative overflow-x-auto">
          {loading ? (
            <div className="p-8 text-center text-gray-500">Loading...</div>
          ) : filteredRequests.length === 0 ? (
            <div className="p-12 text-center text-gray-500">No overtime requests found.</div>
          ) : (
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  {statusFilter === 'pending' && (
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      <input type="checkbox"
                        checked={pendingIds.length > 0 && selectedIds.size === pendingIds.length}
                        onChange={toggleAll}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
                    </th>
                  )}
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Employee</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Start</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">End</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Duration</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Remark</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  {statusFilter === 'pending' && (
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  )}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredRequests.map((req) => (
                  <tr key={req.id} className="hover:bg-gray-50">
                    {statusFilter === 'pending' && (
                      <td className="px-6 py-4 whitespace-nowrap">
                        <input type="checkbox"
                          checked={selectedIds.has(req.id)}
                          onChange={() => toggleSelect(req.id)}
                          disabled={req.status !== 'pending'}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
                      </td>
                    )}
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{req.user_name}</div>
                      <div className="text-sm text-gray-500">{req.staff_no}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{req.date}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{formatDT(req.start_time)}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{formatDT(req.end_time)}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {calcDuration(req.start_time, req.end_time, req.break_time)}
                      {req.break_time > 0 && <span className="ml-1 text-xs text-gray-500">(-{req.break_time}m)</span>}
                    </td>
                    <td
                      className={`px-6 py-4 text-sm text-gray-500 cursor-pointer select-none ${expandedRemarks.has(req.id) ? 'whitespace-normal max-w-xs' : 'max-w-xs truncate'}`}
                      title={expandedRemarks.has(req.id) ? undefined : (req.remark || undefined)}
                      onClick={() => toggleRemark(req.id)}
                    >{req.remark || '-'}</td>
                    <td className="px-6 py-4 whitespace-nowrap">{statusBadge(req.status)}</td>
                    {statusFilter === 'pending' && (
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-2">
                        {req.status === 'pending' && (
                          <>
                            <button onClick={() => openApprove([req.id], 'approved')}
                              className="inline-flex items-center px-2.5 py-1.5 border border-green-300 text-xs font-medium rounded text-green-700 bg-white hover:bg-green-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500">Approve</button>
                            <button onClick={() => openApprove([req.id], 'rejected')}
                              className="inline-flex items-center px-2.5 py-1.5 border border-red-300 text-xs font-medium rounded text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500">Reject</button>
                          </>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Remark modal */}
      {remarkModal && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
              <h3 className="text-lg font-medium text-gray-900 capitalize">
                {remarkModal.status} {remarkModal.ids.length} request(s)
              </h3>
              <button onClick={() => setRemarkModal(null)} className="text-gray-400 hover:text-gray-500">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Remark (optional)</label>
                <textarea
                  value={remark}
                  onChange={(e) => setRemark(e.target.value)}
                  rows={3}
                  placeholder="Add a note for the employee..."
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                />
              </div>
              <div className="flex justify-end space-x-3">
                <button onClick={() => setRemarkModal(null)}
                  className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50">
                  Cancel
                </button>
                <button
                  onClick={submitApproval}
                  disabled={loading}
                  className={`px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white disabled:opacity-50 ${
                    remarkModal.status === 'approved' ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'
                  }`}
                >
                  Confirm {remarkModal.status}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Manager Panel ─────────────────────────────────────────────────────────────

const tabs = [
  { id: 'overtime',   label: 'Overtime Approvals', icon: Clock },
  { id: 'attendance', label: 'Team Attendance', icon: Calendar },
];

const ManagerPanel = () => {
  const [activeTab, setActiveTab] = useState('overtime');

  return (
    <div className="space-y-6 max-w-7xl mx-auto px-4 sm:px-6 md:px-8">
      <div className="flex justify-between items-center mb-2">
        <h1 className="text-2xl font-bold text-gray-900">Manager</h1>
      </div>

      <div className="border-b border-gray-200 bg-white shadow-sm rounded-t-lg px-4 pt-2">
        <nav className="-mb-px flex space-x-8">
          {tabs.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              onClick={() => setActiveTab(id)}
              className={`${
                activeTab === id
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
              } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center`}
            >
              <Icon className="h-5 w-5 mr-2" />
              {label}
            </button>
          ))}
        </nav>
      </div>

      <div className="mt-6">
        {activeTab === 'attendance' && <TeamAttendanceTab />}
        {activeTab === 'overtime'   && <OvertimeApprovalsTab />}
      </div>
    </div>
  );
};

export default ManagerPanel;
